/**
 * color-group-swatches.js
 * Runs once on page load, populates all .color-group-swatches containers.
 *
 * Collection card hover behaviour:
 *   - Injects a temporary overlay <img> over the card gallery on swatch hover
 *   - Updates the card's main anchor href to point to the hovered product
 *   - Reverts both on mouseleave
 *   - Clicking navigates directly to that product
 *
 * Swatch display priority:
 *   1. Hex color from custom.swatch_color (if set)
 *   2. Custom swatch image from custom.swatch_image (if set)
 *   3. Featured image thumbnail (cropped square)
 *
 * Colour name sourced from the product's Colour/Color option value.
 * Loaded globally from theme.liquid.
 */
(function () {
  'use strict';

  const STOREFRONT_TOKEN = '6a2db8e2de7c100bbce9b4dee5bfff84';
  const API_VERSION = '2024-10';
  const shopDomain = window.Shopify?.shop || location.hostname;
  const endpoint = `https://${shopDomain}/api/${API_VERSION}/graphql.json`;
  const cache = {};

  function buildQuery(groupId) {
    return JSON.stringify({
      query: `{
        products(first: 20, query: "tag:color-group-${groupId}") {
          edges {
            node {
              id
              title
              handle
              options {
                name
                optionValues { name }
              }
              featuredImage {
                url(transform: { maxWidth: 800 })
                swatchUrl: url(transform: { maxWidth: 80, maxHeight: 80, crop: CENTER })
              }
              swatchColor: metafield(namespace: "custom", key: "swatch_color") {
                value
              }
              swatchImage: metafield(namespace: "custom", key: "swatch_image") {
                reference {
                  ... on MediaImage {
                    image { url(transform: { maxWidth: 80, maxHeight: 80 }) }
                  }
                }
              }
            }
          }
        }
      }`
    });
  }

  // Get the colour option value from product options
  function getColorName(options) {
    if (!options) return null;
    const colorOption = options.find(o => /colou?r/i.test(o.name));
    return colorOption?.optionValues?.[0]?.name ?? null;
  }

  async function fetchGroup(groupId) {
    if (cache[groupId]) return cache[groupId];
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: buildQuery(groupId)
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const { data } = await res.json();
    const products = (data?.products?.edges ?? []).map(e => e.node);
    cache[groupId] = products;
    return products;
  }

  // Find the card gallery element — the area containing the product image
  function findCard(container) {
    return container.closest('.product-card') || container.closest('product-card');
  }

  function findCardGallery(container) {
    const card = findCard(container);
    if (!card) return null;
    return card.querySelector('.card-gallery, slideshow-component, .product-media-container');
  }

  function findCardLink(container) {
    const card = findCard(container);
    if (!card) return null;
    return (
      card.closest('product-card-link') ||
      card.querySelector('a.product-card__link') ||
      card.querySelector('a[href*="/products/"]')
    );
  }

  function makeSwatch({ href, colorName, productTitle, hexColor, swatchImageUrl, productImageUrl, isCurrent, container }) {
    const a = document.createElement('a');
    a.href = href;
    a.className = 'color-group-swatch' + (isCurrent ? ' color-group-swatch--active' : '');
    a.setAttribute('role', 'listitem');

    const rawLabel = colorName || productTitle;
    // Capitalise first letter
    const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
    a.setAttribute('aria-label', label + (isCurrent ? ' (current)' : ''));
    a.setAttribute('title', label);

    if (hexColor) {
      a.style.setProperty('--swatch-bg', hexColor);
    } else if (swatchImageUrl) {
      a.style.backgroundImage = `url('${swatchImageUrl}')`;
      a.style.backgroundSize = 'cover';
      a.style.backgroundPosition = 'center';
    } else {
      a.style.setProperty('--swatch-bg', '#ccc');
    }

    if (isCurrent) {
      a.setAttribute('aria-current', 'true');
    }

    if (!isCurrent && productImageUrl) {
      const gallery = findCardGallery(container);
      const cardLink = findCardLink(container);
      const originalHref = cardLink?.getAttribute('href') || null;

      let overlay = null;

      a.addEventListener('mouseenter', () => {
        if (cardLink) cardLink.setAttribute('href', href);
        if (gallery) {
          if (!overlay) {
            overlay = document.createElement('img');
            overlay.className = 'color-group-swatch-overlay';
            overlay.alt = label;
          }
          overlay.src = productImageUrl;
          gallery.appendChild(overlay);
        }
      });

      a.addEventListener('mouseleave', () => {
        if (cardLink && originalHref) cardLink.setAttribute('href', originalHref);
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      });
    }

    return a;
  }

  function populateContainer(container, siblings) {
    // Guard handled by data-populated in init()

    const {
      productId: currentRawId,
      productHandle,
      productTitle,
      productColor,
    } = container.dataset;

    if (!siblings.length) {
      container.appendChild(makeSwatch({
        href: `/products/${productHandle}`,
        colorName: null,
        productTitle,
        hexColor: productColor || null,
        swatchImageUrl: null,
        productImageUrl: null,
        isCurrent: true,
        container
      }));
      return;
    }

    siblings.sort((a, b) => a.title.localeCompare(b.title));

    const isCard = !!container.closest('.product-card');

    // Build and append all swatches
    const allSwatches = [];
    siblings.forEach(({ id, title, handle, options, featuredImage, swatchColor, swatchImage }) => {
      const numericId = id.split('/').pop();
      const isCurrent = numericId === currentRawId;
      const hexColor = swatchColor?.value || null;
      const customSwatchImageUrl = swatchImage?.reference?.image?.url ?? null;
      const swatchImageUrl = customSwatchImageUrl || featuredImage?.swatchUrl || null;
      const productImageUrl = featuredImage?.url ?? null;
      const colorName = getColorName(options);

      const swatch = makeSwatch({
        href: `/products/${handle}`,
        colorName,
        productTitle: title,
        hexColor,
        swatchImageUrl,
        productImageUrl,
        isCurrent,
        container
      });

      allSwatches.push({ swatch, isCurrent });
      container.appendChild(swatch);
    });

    // Product page — let swatches wrap freely, no overflow handling
    if (!isCard) return;

    // Collection cards — only append MAX swatches to DOM, rest added on click
    const MAX = window.innerWidth < 750 ? 4 : 6;
    const visible = [];
    const hidden = [];

    // Separate current from others first
    const currentSwatch = allSwatches.find(s => s.isCurrent);
    const others = allSwatches.filter(s => !s.isCurrent);

    // Remove all from container — re-add selectively
    allSwatches.forEach(({ swatch }) => {
      if (swatch.parentNode) swatch.parentNode.removeChild(swatch);
    });

    // Slots for MAX swatches total including current
    const slotsForOthers = currentSwatch ? MAX - 1 : MAX;

    others.forEach(({ swatch }, i) => {
      if (i < slotsForOthers) {
        visible.push(swatch);
      } else {
        hidden.push(swatch);
      }
    });

    // Insert current at start of visible list
    if (currentSwatch) visible.unshift(currentSwatch.swatch);

    // Add only visible swatches to DOM
    visible.forEach(s => container.appendChild(s));

    // Add +N button if needed
    if (hidden.length > 0) {
      const moreBtn = document.createElement('button');
      moreBtn.className = 'color-group-swatch-more';
      moreBtn.textContent = '+' + hidden.length;
      moreBtn.setAttribute('aria-label', 'Show ' + hidden.length + ' more colours');
      moreBtn.addEventListener('click', () => {
        hidden.forEach(s => container.insertBefore(s, moreBtn));
        moreBtn.remove();
        container.classList.add('is-expanded');
      });
      container.appendChild(moreBtn);
    }
  }

  function init() {
    const containers = document.querySelectorAll('.color-group-swatches[data-color-group]');
    if (!containers.length) return;

    const groups = {};
    containers.forEach(container => {
      // Mark as being processed to avoid double processing
      if (container.dataset.populated) return;
      container.dataset.populated = 'true';

      const id = container.dataset.colorGroup;
      if (!groups[id]) groups[id] = [];
      groups[id].push(container);
    });

    if (!Object.keys(groups).length) return;

    Object.entries(groups).forEach(([groupId, containerList]) => {
      fetchGroup(decodeURIComponent(groupId))
        .then(siblings => {
          containerList.forEach(container => populateContainer(container, siblings));
        })
        .catch(err => console.warn('[Color Group Swatches]', groupId, err));
    });
  }

  // Watch for AJAX re-renders (sort, filter, infinite scroll)
  // Disconnect before running init to avoid observer triggering itself
  let debounceTimer;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      observer.disconnect();
      init();
      observer.observe(document.body, { childList: true, subtree: true });
    }, 200);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    init();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
